/** 会话创建时的场景快照；只负责恢复原生 UI，不代表技能已安装或授权。 */
export interface SessionScenario {
  snapshotVersion?: 1;
  id: number;
  name: string;
  icon: string;
  description: string;
  group_id: number;
  group_name: string;
  tags: string;
  system_prompt: string;
  knowledge: string;
  status: number;
  is_hot: boolean;
  is_recommended: boolean;
  usage_count: number;
  sort_order: number;
  expert_name: string;
  greeting: string;
  workflow_steps: string;
  trigger_prompts: string;
  recommended_model: string;
  allow_model_switch: boolean;
  skills: { skill_id: number; skill_name: string; display_name: string; status?: number; version?: string }[];
}

/** 白名单读取场景对象或数据库 JSON；非法身份返回 null，旧引用保留兼容标记。 */
export function normalizeSessionScenario(raw: unknown): SessionScenario | null {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const v = value as Record<string, unknown>;
    if (!Number.isSafeInteger(v.id) || Number(v.id) <= 0 || typeof v.name !== 'string' || !v.name.trim()) return null;
    /** 限制持久化体积，不接受对象、令牌或任意额外属性。 */
    const text = (key: string, limit = 20000): string => typeof v[key] === 'string' ? (v[key] as string).slice(0, limit) : '';
    return {
      ...(v.snapshotVersion === 1 ? { snapshotVersion: 1 as const } : {}),
      id: Number(v.id), name: text('name', 500), icon: text('icon', 2000),
      description: text('description'), group_id: Number(v.group_id) || 0,
      group_name: text('group_name', 500), tags: text('tags', 2000),
      system_prompt: text('system_prompt', 100000), knowledge: text('knowledge', 100000),
      status: typeof v.status === 'number' ? v.status : 1,
      is_hot: v.is_hot === true, is_recommended: v.is_recommended === true,
      usage_count: Number(v.usage_count) || 0, sort_order: Number(v.sort_order) || 0,
      expert_name: text('expert_name', 500), greeting: text('greeting'),
      workflow_steps: text('workflow_steps'), trigger_prompts: text('trigger_prompts'),
      recommended_model: text('recommended_model', 500), allow_model_switch: v.allow_model_switch !== false,
      skills: (Array.isArray(v.skills) ? v.skills : []).slice(0, 100)
        .filter((skill): skill is Record<string, unknown> => !!skill && typeof skill === 'object'
          && Number.isSafeInteger(skill.skill_id) && skill.skill_id > 0 && typeof skill.skill_name === 'string')
        .map((skill) => ({
          skill_id: Number(skill.skill_id), skill_name: String(skill.skill_name).slice(0, 500),
          display_name: typeof skill.display_name === 'string' ? skill.display_name.slice(0, 500) : '',
          ...(typeof skill.status === 'number' ? { status: skill.status } : {}),
          ...(typeof skill.version === 'string' ? { version: skill.version.slice(0, 100) } : {}),
        })),
    };
  } catch {
    return null;
  }
}

/** 原生场景选择和 App 使用同一版本化快照，不将运行时就绪状态写入快照。 */
export function createSessionScenario(raw: unknown): SessionScenario | null {
  const scenario = normalizeSessionScenario(raw);
  return scenario ? { ...scenario, snapshotVersion: 1 } : null;
}
